/**
 * MONSTER TOWN — "DO YOU WANT THIS?" HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * THE ONE PLACE IN THE GAME WHERE A MIS-TAP USED TO COST SOMETHING.
 *
 * The town shop is a room with fifteen identical white plinths, each with a
 * small shiny thing on top, and tapping one used to buy it outright: coins left
 * the purse, no panel opened, nothing on screen ever said what had been bought.
 * A three-year-old walking round tapping the shiny things drained the purse
 * without understanding that a purchase had happened at all — and unlike a
 * placement, a purchase has no undo.
 *
 * Everything else in the game forgives a mis-tap, so this does too now. Tapping
 * a plinth OFFERS; this card asks. It is deliberately built out of the three
 * things a kid needs and nothing else:
 *
 *   1. a big picture of the actual thing,
 *   2. its price as countable coins (big = 100, middle = 10, little = 1 — the
 *      same language the plinth speaks in 3D, so the card explains the shelf),
 *   3. one big YES and one big NO.
 *
 * NO READING (LAW #4) and NO DEAD END: the card is built on `panelShell`, so the
 * 112 px back button exists before anything else does, and the NO button is the
 * same size as the YES — backing out is never the small, hard, grey option.
 *
 * When a kid cannot afford something yet there is no YES at all, just the price
 * and the way out. A button that says yes and then refuses is worse than no
 * button, and "not yet" is never a scold (brief).
 */

import { bus } from '../core/events'
import { getPlaceable } from '../core/placeable'
import type { InstanceId, PlaceableId, SceneId } from '../core/types'
import { drawPieceIcon } from '../render/icons2d'
import { button, el } from './dom'
import { iconEl } from './icons'
import { panelShell, type PanelHost } from './panels'

export const BUY_PANEL = 'buy'

interface PendingOffer {
  defId: PlaceableId
  sceneId: SceneId
  instanceId: InstanceId
  cost: number
  have: number
  affordable: boolean
}

let pending: PendingOffer | null = null

/** The thing itself, drawn big. Same picture as its tile in the build drawer. */
function bigPicture(defId: PlaceableId, size: number): HTMLElement {
  const wrap = el('div', 'mt-buy-pic')
  const canvas = el('canvas', '')
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  canvas.width = Math.round(size * dpr)
  canvas.height = Math.round(size * dpr)
  canvas.style.width = `${size}px`
  canvas.style.height = `${size}px`
  const c = canvas.getContext('2d')
  const def = getPlaceable(defId)
  if (c !== null && def !== undefined) {
    c.scale(dpr, dpr)
    drawPieceIcon(c, size, def)
  }
  wrap.append(canvas)
  return wrap
}

/**
 * A price you can COUNT. Big coin = 100, middle = 10, little = 1 — the same
 * denominations `coinPrice` stacks on the plinth in 3D, so the card is a
 * close-up of the shelf label rather than a second, unrelated notation.
 */
function coinPrice(amount: number): HTMLElement {
  const row = el('div', 'mt-buy-price')
  const add = (n: number, cls: string): void => {
    for (let i = 0; i < n; i++) {
      const c = el('span', `mt-buy-coin ${cls}`)
      c.append(iconEl('coin', cls === 'big' ? 56 : cls === 'mid' ? 40 : 28))
      row.append(c)
    }
  }
  add(Math.min(5, Math.floor(amount / 100)), 'big')
  add(Math.floor((amount % 100) / 10), 'mid')
  add(amount % 10, 'small')
  if (row.childElementCount === 0) add(1, 'small')
  return row
}

/**
 * Register the offer card and the tap that opens it.
 *
 * Costs one idle listener if the economy crew is not installed: `shop:offer` is
 * declaration-merged by the venue crew, so this file imports nothing from it.
 */
export function registerBuyPanel(panels: PanelHost): void {
  panels.register(BUY_PANEL, () => {
    const o = pending
    const close = (): void => {
      if (o !== null) bus.emit('shop:cancel', { defId: o.defId })
      pending = null
      panels.hide()
    }
    const parts = panelShell({ icon: 'bag', onBack: close })
    parts.root.classList.add('mt-buy')
    if (o === null) return parts

    const card = el('div', 'mt-buy-card')
    card.append(bigPicture(o.defId, 200))
    card.append(coinPrice(o.cost))
    parts.body.append(card)

    // What is in the purse right now, beside what this costs, so "not yet" is a
    // picture of two amounts rather than a refusal.
    const purse = el('div', 'mt-buy-purse')
    purse.append(iconEl('coin', 44))
    const have = el('span', 'mt-num')
    have.textContent = String(o.have)
    purse.append(have)
    parts.body.append(purse)

    const no = button(
      { icon: 'close', label: 'No thank you', className: 'big paper', cue: 'ui_back' },
      close,
    )
    parts.foot.append(no)

    if (o.affordable) {
      const yes = button(
        { icon: 'check', label: 'Yes please', className: 'big primary', iconSize: 64 },
        () => {
          bus.emit('shop:confirm', { defId: o.defId, sceneId: o.sceneId, instanceId: o.instanceId })
          pending = null
          panels.hide()
        },
      )
      parts.foot.append(yes)
    } else {
      // Not yet. The price coins get a friendly wobble; there is no yes to press
      // and nothing here says no to the kid.
      card.dataset['short'] = 'true'
      bus.emit('audio:cue', { cue: 'nope_soft' })
    }
    return parts
  })

  bus.on('shop:offer', (e) => {
    if (e.owned) return // already yours, forever — nothing to decide
    pending = {
      defId: e.defId,
      sceneId: e.sceneId,
      instanceId: e.instanceId,
      cost: e.cost,
      have: e.have,
      affordable: e.affordable,
    }
    bus.emit('ui:panel', { panel: BUY_PANEL })
  })

  // Whatever closes the card — the back button, another panel, leaving the shop
  // — must retire the offer, or a stale one reopens with the next `ui:panel`.
  bus.on('ui:panel', (e) => {
    if (e.panel !== BUY_PANEL) pending = null
  })
  bus.on('scene:left', () => {
    pending = null
  })
}

/** Test/QA surface: what the card is currently offering, if anything. */
export function pendingOffer(): { defId: string; cost: number; affordable: boolean } | null {
  return pending === null
    ? null
    : { defId: String(pending.defId), cost: pending.cost, affordable: pending.affordable }
}
